import React, { useEffect, useMemo, useState } from "react";
import { MapPin, RefreshCw, Search, X } from "lucide-react";
import toast from "react-hot-toast";

const isValidLat = (v) => v !== "" && !Number.isNaN(Number(v)) && Number(v) >= -90 && Number(v) <= 90;
const isValidLng = (v) => v !== "" && !Number.isNaN(Number(v)) && Number(v) >= -180 && Number(v) <= 180;

export default function RestaurantLatLongModal({
  open,
  onClose,
  getRestaurantPreciseLocation,
  updateRestaurantLatLong,
}) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await getRestaurantPreciseLocation();
      if (res?.responseCode === "00") {
        setRows(
          (res.response || []).map((x) => ({
            ...x,
            latitude: x.latitude || "",
            longtitude: x.longtitude || x.lontitude || "",
          }))
        );
      } else {
        toast.error(res?.responseMessage || "Failed to fetch restaurant locations");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) loadData();
  }, [open]);

  const filtered = useMemo(() => {
    const s = q.toLowerCase().trim();
    if (!s) return rows;
    return rows.filter(
      (r) =>
        String(r.restaurant_name || "").toLowerCase().includes(s) ||
        String(r.merchant_id || "").includes(s)
    );
  }, [rows, q]);

  const updateRow = (merchantId, key, value) => {
    setRows((prev) =>
      prev.map((r) =>
        r.merchant_id === merchantId ? { ...r, [key]: value } : r
      )
    );
  };

  const handleUpdate = async (row) => {
    if (!isValidLat(row.latitude)) {
      toast.error("Enter valid latitude between -90 and 90");
      return;
    }

    if (!isValidLng(row.longtitude)) {
      toast.error("Enter valid longitude between -180 and 180");
      return;
    }

    setSavingId(row.merchant_id);
    try {
      const res = await updateRestaurantLatLong({
        fk_user_id: row.merchant_id,
        latitude: row.latitude,
        longtitude: row.longtitude,
      });

      if (res?.responseCode === "00") {
        toast.success(res?.responseMessage || "Location updated");
      } else {
        toast.error(res?.responseMessage || "Update failed");
      }
    } finally {
      setSavingId(null);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3">
      <div className="flex max-h-[88vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b p-4">
          <div className="flex items-center gap-3">
            <div className="grid h-9 w-9 place-items-center rounded-xl bg-[#01C37D]/10 text-[#01C37D]">
              <MapPin size={18} />
            </div>
            <div>
              <div className="text-sm font-semibold">Update Restaurant Latitude / Longitude</div>
              <div className="text-xs text-gray-500">Edit restaurant precise delivery location</div>
            </div>
          </div>

          <button className="rounded-xl border p-2 hover:bg-gray-50" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-b p-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
            <input
              className="h-10 w-full rounded-xl border pl-9 pr-3 text-sm outline-none"
              placeholder="Search restaurant / merchant id"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>

          <button
            className="inline-flex h-10 items-center gap-2 rounded-xl border px-3 text-sm hover:bg-gray-50"
            onClick={loadData}
            disabled={loading}
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>

        <div className="overflow-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="sticky top-0 bg-[#01C37D] text-white">
              <tr>
                <th className="px-4 py-2">Merchant ID</th>
                <th className="px-4 py-2">Restaurant</th>
                <th className="px-4 py-2">Latitude</th>
                <th className="px-4 py-2">Longitude</th>
                <th className="px-4 py-2 text-right">Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                    Loading restaurant locations…
                  </td>
                </tr>
              ) : filtered.length ? (
                filtered.map((row) => (
                  <tr key={row.merchant_id} className="border-t hover:bg-gray-50/70">
                    <td className="px-4 py-2 font-semibold">{row.merchant_id}</td>
                    <td className="px-4 py-2">{row.restaurant_name || "—"}</td>
                    <td className="px-4 py-2">
                      <input
                        className="h-9 w-40 rounded-xl border px-3 text-sm outline-none focus:border-[#01C37D]"
                        value={row.latitude}
                        onChange={(e) => updateRow(row.merchant_id, "latitude", e.target.value)}
                        placeholder="Latitude"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        className="h-9 w-40 rounded-xl border px-3 text-sm outline-none focus:border-[#01C37D]"
                        value={row.longtitude}
                        onChange={(e) => updateRow(row.merchant_id, "longtitude", e.target.value)}
                        placeholder="Longitude"
                      />
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button
                        className="h-9 rounded-xl bg-[#01C37D] px-4 text-xs font-semibold text-white hover:bg-[#01C37D]/90 disabled:opacity-50"
                        disabled={savingId === row.merchant_id}
                        onClick={() => handleUpdate(row)}
                      >
                        {savingId === row.merchant_id ? "Updating…" : "Update"}
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                    No restaurant found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="border-t bg-gray-50 px-4 py-2 text-xs text-gray-500">
          Longitude key is sent as <b>longtitude</b> to match backend payload.
        </div>
      </div>
    </div>
  );
}